import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  Select,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core"
import { useForm } from "@mantine/form"
import { useDisclosure } from "@mantine/hooks"
import { modals } from "@mantine/modals"
import { notifications } from "@mantine/notifications"
import { useCallback, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { PageHeader, PageLayout } from "../../../components/crud"
import type { Column } from "../../../components/DataTable"
import { DataTable, useDataTable } from "../../../components/DataTable"
import { useUser } from "../../user/hooks/useUser"
import {
  useCreateNotificationAssignment,
  useDeleteNotificationAssignment,
  useRoles,
} from "../hooks/useNotificationAssignments"
import type {
  NotificationAssignment,
  NotificationAssignmentFormValues,
} from "../models/notificationAssignment"
import {
  NOTIFICATION_TYPES,
  NotificationAssignmentFormSchema,
  NotificationAssignmentSchema,
} from "../models/notificationAssignment"

export default function NotificationAssignmentList() {
  const { t } = useTranslation()
  const { data: userData } = useUser()
  const canManage = userData?.can?.manageNotificationAssignments ?? false
  const { data: roles } = useRoles()

  const roleOptions = (roles ?? []).map((r) => ({
    value: r.name,
    label: r.description,
  }))
  const notificationOptions = NOTIFICATION_TYPES.map((n) => ({
    value: n.value,
    label: n.label,
  }))

  const {
    data,
    isLoading,
    page,
    pageSize,
    sort,
    sortDir,
    handleSortChange,
    handlePageChange,
    filters,
    updateFilters,
  } = useDataTable<NotificationAssignment>({
    queryKey: "notification-assignments",
    url: "notification-assignment",
    schema: NotificationAssignmentSchema,
    defaultSort: { field: "notification_key", dir: "asc" },
    defaultPageSize: 50,
  })

  const [opened, { open, close }] = useDisclosure(false)
  const createMutation = useCreateNotificationAssignment()
  const deleteMutation = useDeleteNotificationAssignment()

  const form = useForm<NotificationAssignmentFormValues>({
    initialValues: { role: "", notification_key: "" },
    validate: (values) => {
      const result = NotificationAssignmentFormSchema.safeParse(values)
      if (!result.success) {
        return Object.fromEntries(
          result.error.issues.map((e) => [e.path[0], e.message]),
        )
      }
      return {}
    },
  })

  const handleCreate = useCallback(
    (values: NotificationAssignmentFormValues) => {
      createMutation.mutate(values, {
        onSuccess: () => {
          notifications.show({
            color: "green",
            title: t("common.success"),
            message: t("notificationAssignments.createSuccess"),
          })
          close()
          form.reset()
        },
        onError: () => {
          notifications.show({
            color: "red",
            title: t("common.error"),
            message: t("common.error"),
          })
        },
      })
    },
    [createMutation, close, form, t],
  )

  const handleDelete = useCallback(
    (role: string, notificationKey: string) => {
      modals.openConfirmModal({
        title: t("notificationAssignments.confirmDelete"),
        children: (
          <Text size="sm">
            {t("notificationAssignments.confirmDeleteMessage")}
          </Text>
        ),
        labels: { confirm: t("common.delete"), cancel: t("common.cancel") },
        confirmProps: { color: "red" },
        onConfirm: () => {
          deleteMutation.mutate(
            { role, notificationKey },
            {
              onSuccess: () => {
                notifications.show({
                  color: "green",
                  title: t("common.success"),
                  message: t("notificationAssignments.deleteSuccess"),
                })
              },
            },
          )
        },
      })
    },
    [deleteMutation, t],
  )

  const columns: Column<NotificationAssignment>[] = [
    {
      key: "notification_key",
      label: t("notificationAssignments.notification"),
      sortable: true,
      render: (v) => {
        const info = NOTIFICATION_TYPES.find(
          (n) => n.value === v.notification_key,
        )
        return (
          <Badge color="violet" variant="light" size="sm">
            {info?.label ?? v.notification_key}
          </Badge>
        )
      },
    },
    {
      key: "role",
      label: t("notificationAssignments.role"),
      sortable: true,
      render: (v) => <Text size="sm">{v.role}</Text>,
    },
  ]

  return (
    <PageLayout
      header={
        <PageHeader
          title={t("notificationAssignments.title")}
          actions={
            canManage ? (
              <Button onClick={open} leftSection={<i className="fa fa-plus" />}>
                <Trans i18nKey="notificationAssignments.create" />
              </Button>
            ) : undefined
          }
        />
      }
    >
      <Card withBorder p="sm" radius="md" mb="md">
        <Group grow align="flex-end">
          <Select
            label={t("notificationAssignments.notification")}
            placeholder={t("notificationAssignments.allNotifications")}
            data={notificationOptions}
            clearable
            value={(filters.notification_key as string | undefined) ?? null}
            onChange={(val) =>
              updateFilters({ notification_key: val || undefined })
            }
          />
          <TextInput
            label={t("notificationAssignments.role")}
            placeholder={t("notificationAssignments.role")}
            value={(filters.role as string) ?? ""}
            onChange={(e) =>
              updateFilters({ role: e.currentTarget.value || undefined })
            }
          />
        </Group>
      </Card>
      <DataTable<NotificationAssignment>
        columns={columns}
        data={data?.items ?? []}
        total={data?.total ?? 0}
        page={page}
        pageSize={pageSize}
        sort={sort}
        sortDir={sortDir}
        loading={isLoading}
        emptyMessage={t("notificationAssignments.empty")}
        keyExtractor={(v) => `${v.role}-${v.notification_key}`}
        onSortChange={handleSortChange}
        onPageChange={handlePageChange}
        actions={(v) =>
          canManage ? (
            <Group gap={4} justify="flex-end" wrap="nowrap">
              <Tooltip label={t("common.delete")}>
                <ActionIcon
                  variant="subtle"
                  size="md"
                  color="red"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleDelete(v.role, v.notification_key)
                  }}
                >
                  <i className="fa fa-trash" />
                </ActionIcon>
              </Tooltip>
            </Group>
          ) : undefined
        }
      />

      <Modal
        opened={opened}
        onClose={close}
        title={t("notificationAssignments.createTitle")}
      >
        <form onSubmit={form.onSubmit(handleCreate)}>
          <Select
            label={t("notificationAssignments.role")}
            placeholder={t("notificationAssignments.role")}
            data={roleOptions}
            searchable
            withAsterisk
            {...form.getInputProps("role")}
          />
          <Select
            label={t("notificationAssignments.notification")}
            placeholder={t("notificationAssignments.notification")}
            data={notificationOptions}
            withAsterisk
            mt="md"
            {...form.getInputProps("notification_key")}
          />
          <Group justify="flex-end" mt="xl">
            <Button variant="default" onClick={close}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={createMutation.isPending}>
              {t("common.create")}
            </Button>
          </Group>
        </form>
      </Modal>
    </PageLayout>
  )
}
